import { useQuery } from "@tanstack/react-query";

import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { authHeader } from "@/utils/headers";

interface PreviewUrlResponse {
  success: boolean;
  preview_url: string;
  expires_in: number;
  file_id?: string;
  filename?: string;
  storage_key?: string;
  message?: string;
}

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === "string" && value.length > 0;

export const isBrowserRenderableMediaUrl = (url: string | null | undefined): url is string => {
  if (!isNonEmptyString(url)) {
    return false;
  }

  return (
    url.startsWith("http://") ||
    url.startsWith("https://") ||
    url.startsWith("data:") ||
    url.startsWith("blob:")
  );
};

const fetchPreviewUrl = async (
  endpoint: string,
  params: URLSearchParams,
  userToken: string,
  errorPrefix: string,
) => {
  const response = await fetch(`${endpoint}?${params.toString()}`, {
    headers: authHeader(userToken),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `${errorPrefix}: ${response.statusText}`);
  }

  const data = (await response.json()) as PreviewUrlResponse;

  if (!data.success || !isNonEmptyString(data.preview_url)) {
    throw new Error(data.message || errorPrefix);
  }

  return data.preview_url;
};

export const useVectorStoreFileContentUrl = (
  fileId: string | null,
  filename: string | null,
  expiration = 3600, // Default 1 hour, max 24 hours (86400s)
) => {
  const auth = useAuth();
  const userContext = useUser();
  const workspaceId = userContext?.activeWorkspaceId;

  const query = useQuery({
    queryKey: ["vector-store-file-content", fileId, filename, workspaceId, expiration],
    queryFn: async () => {
      const params = new URLSearchParams({
        workspace_id: workspaceId!.toString(),
        file_id: fileId!,
        filename: filename!,
        expiration: expiration.toString(),
      });

      return fetchPreviewUrl(
        ENDPOINTS.vector_store_file_content,
        params,
        auth!.userToken!,
        "Failed to generate preview URL",
      );
    },
    enabled: !!fileId && !!filename && !!workspaceId,
    staleTime: (expiration - 60) * 1000, // Refresh 1 minute before expiration
    cacheTime: expiration * 1000, // Keep in cache for the duration of the URL validity
  });

  return {
    previewUrl: query.data,
    isLoading: query.isLoading,
    error: query.error,
  };
};

export const useOutputMediaContentUrl = (storageKey: string | null, expiration = 3600) => {
  const auth = useAuth();

  const query = useQuery({
    queryKey: ["output-media-content", storageKey, expiration],
    queryFn: async () => {
      const params = new URLSearchParams({
        storage_key: storageKey!,
        expiration: expiration.toString(),
      });

      return fetchPreviewUrl(
        ENDPOINTS.output_media_content,
        params,
        auth!.userToken!,
        "Failed to generate output media preview URL",
      );
    },
    enabled: !!storageKey && !!auth?.userToken,
    staleTime: (expiration - 60) * 1000,
    cacheTime: expiration * 1000,
  });

  return {
    previewUrl: query.data,
    isLoading: query.isLoading,
    error: query.error,
  };
};
